import React, {createRef} from "react"

import FinderHeader from '../header/finder-header'
import TaskFinderMain from "./task-finder-main"

import {finderLoadProjectTasks} from "../functions/load-project-tasks"
import {finderLoadRecentTasks} from "../functions/load-recent-tasks"
import Task from "../../../model2/task/task"
import FinderTasksCollection from "../../../model2/task/finder/collection"
import {SystemStatusFilter} from "./@types";
import asyncSessionLoader from "../../../gd/data/async-session-loader"

const MIN_SEARCH_TEXT_LENGTH = 2



type Props = {
    className?          : string

    selectedTaskId?     : ID
    parentProjectId?    : ID | null
    excludedTasksIds?   : ID[]

    noStatusSelector?   : boolean
    noHeader?           : boolean
    noSubtasks?         : boolean
    showRecent?         : boolean
    alwaysShowHome?     : boolean

    onBack?             : func
    onClickTask?        : {(task: Task): void}
    onTab?              : {():void}
    onClose?            : func
    systemStatusFilter? : SystemStatusFilter
}

type State = {
    search          : string

    isSearching     : boolean
    statusId        : SystemStatusFilter

    parentProjectId : ID | null
    data            : any
}

export default class TaskFinder extends React.Component<Props, State> {

    private finderRef: any = createRef()
    private delayedTasksSearch: any

    static defaultProps = {
        excludedTasksIds: []
    }

    constructor(props: Props) {
        super(props);

        this.state = {
            search          : '',
            isSearching     : false,
            statusId        : this.props.systemStatusFilter || SystemStatusFilter.OPEN,

            parentProjectId : this.props.parentProjectId ? this.props.parentProjectId : null,
            data            : null
        }

        this.delayedTasksSearch = _.debounce(this.tasksSearch, 500)
    }

    componentDidMount() {
        this.loadData()
    }

    componentWillUnmount() {
        this.delayedTasksSearch.cancel()
    }

    loadData = async () => {
        // show root projects list (no data) if no parentProjectId is selected and showRecent is false
        if (!this.state.parentProjectId && !this.props.showRecent) {
            this.setState({
                data: {}
            })
            return
        }

        let data: any = {}
        if (this.props.showRecent) {
            data = await finderLoadRecentTasks(this.props.excludedTasksIds)
        } else if (this.state.parentProjectId) {
            const project = gd.session.projects.get(this.state.parentProjectId)
            if (project && !project.isWorkspace) {
                data = await finderLoadProjectTasks(this.state.parentProjectId, this.state.statusId, this.props.excludedTasksIds)
            }
        }

        this.setState({
            data: data
        })
    }

    clearData = () => {this.setState({data: null})}

    onSearchChange = (value: string) => {
        const isSearching = value.length >= MIN_SEARCH_TEXT_LENGTH
        if (this.state.search && !value) {
            this.onSearchCancel()
        } else {
            this.setState({
                search      : value,
                isSearching : isSearching,
                data        : null
            }, ()=>{
                if (isSearching) this.delayedTasksSearch()
            })
        }
    }

    onSearchCancel = () => {
        this.clearData()
        this.setState({
            isSearching : false,
            search      : '',
            data        : null
        }, ()=>{this.loadData()})
    }

    tasksSearch = async () => {
        if (!this.state.search) {
            this.onSearchCancel()
            return
        }

        if (this.state.search.length < MIN_SEARCH_TEXT_LENGTH) {
            return
        }

        let data: any[] = []
        try {
            const response = await gd.api.Search.tasksV414(
                this.state.search,
                this.state.statusId,
            ).promise()

            data = _.filter(response.result, (task: any) => {
                return !this.props.excludedTasksIds?.includes(task.id)
            })

            await asyncSessionLoader(gd.session.findMissing({
                projects  : _.uniq(_.map(data, 'projectId')),
                taskTypes : _.uniq(_.map(data, 'taskTypeId'))
            }))
        } catch (error: any) {
            data = []
        }

        this.setState({
            isSearching : false,
            data        : FinderTasksCollection.initFromJson(data)
        })
    }

    onClickTask = (task: Task) => {
        this.props.onClickTask?.(task)
    }

    onTab = () => {
        this.props.onTab?.()
    }

    onClickCompany = () => {
        this.setState({
            parentProjectId : null,
            data            : null
        }, ()=>{
            this.loadData()
        })
    }

    onClickProject = (id: ID) => {
        this.setState({
            parentProjectId : id,
            data            : null
        }, ()=>{
            this.loadData()
        })
    }

    onStatusSelect = (value: SystemStatusFilter) => {
        this.setState({
            statusId: value
        }, ()=>{
            if (!this.state.search) this.loadData()
            else                    this.tasksSearch()
        })
    }

    onClose = () => {
        this.props.onClose?.()
    }

    onKeyDown = (event: any) => {
        this.finderRef.current.onKeyDown(event)
    }

    render() {
        const classes = Classes("gd-finder", this.props.className, {
            'no-header': this.props.noHeader
        })

        return (
            <div className={classes}>
                {!this.props.noHeader &&
                    <FinderHeader
                        title               = "Select a task"
                        inputPlaceholder    = "Search by task # or name"
                        search              = {this.state.search}
                        statusId            = {this.state.statusId}
                        onBack              = {this.props.onBack}
                        onSearchChange      = {this.onSearchChange}
                        onStatusSelect      = {!this.props.noStatusSelector ? this.onStatusSelect : null}
                        onSearchCancel      = {this.onSearchCancel}
                        onClose             = {this.onClose}
                    />
                }

                <TaskFinderMain
                    ref             = {this.finderRef}
                    companyId       = {gd.companyId}
                    data            = {this.state.data}
                    isSearching     = {this.state.isSearching}
                    selectedTaskId  = {this.props.selectedTaskId}
                    parentProjectId = {this.state.parentProjectId}
                    search          = {this.state.search}
                    noSubtasks      = {this.props.noSubtasks}
                    showRecent      = {this.props.showRecent}
                    alwaysShowHome  = {this.props.alwaysShowHome}
                    onClickCompany  = {this.onClickCompany}
                    onClickProject  = {this.onClickProject}
                    onClickTask     = {this.onClickTask}
                    onTab           = {this.onTab}
                />
            </div>
        )
    }
}